import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, ListGroup, Image, Form, Button, Card, Modal } from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import Message from "../Components/Message";
import { addToCart, removeFromCart } from "../slices/cartSlice";
import BackButton from "../Components/BackButton";
import PriceWithSale from "../Components/PriceWithSale";
import { getEffectivePrice } from "../utils/salePricing";
import { buildProductUrl, getCurrentRoute } from "../utils/navigation";

const CartScreen = () => {
  const LIMIT = 10;

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showLimitModal, setShowLimitModal] = useState(false);

  const { cartItems, itemsPrice } = useSelector((state) => state.cart);
  const { userInfo } = useSelector((state) => state.auth);
  const isTieredUser = Boolean(userInfo?.tier?.id);

  // ✅ CartScreen should SET qty (replace), not increment
  const setQtyHandler = (item, qty) => {
    if (!isTieredUser && qty > LIMIT) {
      setShowLimitModal(true);
      return;
    }
    const nextQty = qty;
    dispatch(
      addToCart({
        ...item,
        qty: nextQty,
        replaceQty: true, // ✅ important
      })
    );
  };

  const removeFromCartHandler = (id) => {
    dispatch(removeFromCart(id));
  };

  const checkoutHandler = () => {
    navigate(userInfo ? '/shipping' : '/login?redirect=/shipping');
  };

  const itemsCount = cartItems.reduce((acc, item) => acc + Number(item.qty || 0), 0);
  const subtotal = Number(itemsPrice ?? 0).toFixed(2);
  const displaySubtotal = cartItems
    .reduce((acc, item) => {
      const unitPrice = isTieredUser
        ? item?.tierPriceApplied
          ? Number(item.price ?? 0)
          : Number(item.originalPrice ?? item.price ?? 0)
        : getEffectivePrice({
            price: Number(item.originalPrice ?? item.price ?? 0),
            salePrice: item.salePrice,
            saleStartAt: item.saleStartAt,
            saleEndAt: item.saleEndAt,
          });
      return acc + unitPrice * Number(item.qty ?? 0);
    }, 0)
    .toFixed(2);

  return (
    <>
      <BackButton />
      <Row>
        <Col md={8}>
          <h1 style={{ marginBottom: "20px" }}>Shopping Cart</h1>

          {cartItems.length === 0 ? (
            <Message>
              Your cart is empty{" "}
              <BackButton
                to="/"
                variant="link"
                className="btn btn-link p-0 align-baseline"
              >
                Go Back
              </BackButton>
            </Message>
          ) : (
            <ListGroup variant="flush">
              {cartItems.map((item) => {
                const maxQty = isTieredUser
                  ? Number(item.stockQty ?? 0) || 0
                  : Math.min(LIMIT, Number(item.stockQty ?? LIMIT) || LIMIT);
                const minQty = 1;
                const qtyOptions =
                  maxQty > 0
                    ? Array.from(
                        { length: Math.max(0, maxQty - minQty + 1) },
                        (_, idx) => minQty + idx
                      )
                    : [];

                return (
                  <ListGroup.Item key={item._id || item.product}>
                    <Row className="align-items-center">
                      <Col md={2}>
                        <Image src={item.image} alt={item.displayName || item.title || item.name} fluid rounded />
                      </Col>

                      <Col md={3}>
                        <Link
                          to={buildProductUrl(item._id || item.product, location)}
                          state={{ from: getCurrentRoute(location) }}
                        >
                          {item.displayName || item.title || item.name}
                        </Link>
                      </Col>

                      <Col md={2}>
                        <PriceWithSale
                          price={Number(item.originalPrice ?? item.price)}
                          salePrice={item.salePrice}
                          saleStartAt={item.saleStartAt}
                          saleEndAt={item.saleEndAt}
                          tierPrice={item.tierPriceApplied ? item.price : null}
                          showTierTag={Boolean(item.tierPriceApplied)}
                          forceBasePrice={
                            Boolean(
                              (item.ignoreSale || isTieredUser) &&
                                !item.tierPriceApplied
                            )
                          }
                        />
                      </Col>

                      <Col md={3}>
                        <Form.Control
                          as="select"
                          value={item.qty}
                          onChange={(e) => setQtyHandler(item, Number(e.target.value))}
                        >
                          {qtyOptions.map((optionQty) => (
                            <option key={optionQty} value={optionQty}>
                              {optionQty}
                            </option>
                          ))}
                        </Form.Control>

                        {!isTieredUser && Number(item.qty) >= LIMIT && (
                          <small className="text-muted d-block mt-1">
                            Max {LIMIT} per item
                          </small>
                        )}
                      </Col>

                      <Col md={2} className="text-end">
                        <Button
                          type="button"
                          variant="light"
                          onClick={() => removeFromCartHandler(item._id || item.product)}
                        >
                          <FaTrash />
                        </Button>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          )}
        </Col>

        <Col md={4}>
          <Card>
            <ListGroup variant="flush">
              <ListGroup.Item>
                <h2>Subtotal ({itemsCount}) items</h2>$
                {isTieredUser ? displaySubtotal : subtotal}
              </ListGroup.Item>

              <ListGroup.Item>
                <div className="d-flex gap-2">
                  <Button
                    type="button"
                    className="btn-block"
                    disabled={cartItems.length === 0}
                    onClick={checkoutHandler}
                    size="sm"
                  >
                    Proceed To Checkout
                  </Button>
                </div>

                <div className="mt-2">
                  <small className="text-muted">
                    See our{' '}
                    <Link to="/shipping/policy">Shipping & Delivery Policy</Link> and{' '}
                    <Link to="/returns/policy">Return & Refund Policy</Link>.
                  </small>
                </div>
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>
      </Row>
      <Modal
        show={showLimitModal}
        onHide={() => setShowLimitModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Large quantity request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Orders over {LIMIT} units qualify for additional discounts. Please{" "}
          <Link to="/contact">contact us</Link> and call to place a larger order.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowLimitModal(false)}
          >
            Close
          </Button>
          <Button
            as={Link}
            to="/contact"
            variant="primary"
            onClick={() => setShowLimitModal(false)}
          >
            Contact Us
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CartScreen;
